import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { NbMenuItem } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, combineLatest, filter } from 'rxjs';
import { Tutor, TutorService, TutorSettingItem } from '../../services/tutor.service';
import { AuthService, UserRoles } from '../../services/auth.service';
import { ChatPreviewService } from '../../services/chat-preview.service';
import { ChatService } from '../../services/chat.service';
import {
  SettingsSection,
  filterSectionsByAvailableSettings,
  SECTIONS_ELSEWHERE,
} from '../../pages/tutors/tutor-id/ai-settings/ai-settings.sections';
import { AgentOrbType, ORB_BY_TEMPLATE } from '../agent-orb/agent-orb.component';
import { ORBI_IDLE } from '../orbi-lab/orbi-lab.component';
import { AgentRefreshService } from '../../agent/agent-refresh.service';

/** Kopfzeilen-Orbi (Marcel, 28.09.2026): "Try me" im Wechsel mit je einer
 *  Bewegung — zweimal rechts, 3× großer Hüpfer, Kicher-Hüpfer, Verstecken &
 *  Erschrecken, Hüpfen + Lachen, Pfeifen — nach jeder Bewegung 2,5s normal
 *  schauen, dann Loop. Rechts neben dem Orbi steht nichts mehr, anders als
 *  auf der Agent-Kachel darf er hier nach rechts springen. */
const HEADER_ORBI_SCRIPT = [
  'try-me', 'hop-right', ORBI_IDLE,
  'try-me', 'hop', 'hop', 'hop', ORBI_IDLE,
  'try-me', 'giggle-bounce', ORBI_IDLE,
  'try-me', 'hide-surprise', ORBI_IDLE,
  'try-me', 'hop-laugh', ORBI_IDLE,
  'try-me', 'idle-whistle', ORBI_IDLE,
];

@Component({
  selector: 'app-tutor-menu',
  templateUrl: './tutor-menu.component.html',
  styleUrl: './tutor-menu.component.scss',
})
export class TutorMenuComponent implements AfterViewInit, OnDestroy {
  items: NbMenuItem[] = [];
  form: any;
  tutorId: string | null = null;
  tutor: Tutor | undefined;

  loading = true;

  /** The agent pages sit inside the agent shell (`TutorShellComponent`, route
   *  `tutors/:tutorId`), which renders this menu ONCE around the router
   *  outlet — header, Orbi and menu stay put while the pages change. Each
   *  page still wraps itself in `<app-tutor-menu>`; inside the shell that
   *  inner instance only passes its content through. */
  readonly inline = !!inject(TutorMenuComponent, { optional: true, skipSelf: true });

  private tutorsSub?: Subscription;
  private settingsSub?: Subscription;
  private routerSub?: Subscription;
  private settingsLoad?: Subscription;
  private tutorLoad?: Subscription;
  /** Memoised structural fingerprint of the rendered sections. We
   *  rebuild `items` only when the fingerprint changes — see the
   *  `tutorSettings$` subscriber for why. */
  private lastSectionKeys = '';

  constructor(
    protected t: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
    protected tutorService: TutorService,
    private auth: AuthService,
    private cdr: ChangeDetectorRef,
    protected chatPreviewService: ChatPreviewService,
    private chatService: ChatService
  ) {
    if (this.inline) {
      this.loading = false;
      return;
    }

    this.tutorId = this.route.snapshot.paramMap.get('tutorId');

    if (!this.tutorService.tutorTemplates$.value.length) {
      this.tutorService.listTutorTemplates().subscribe();
    }

    this.loadAgent(this.tutorId!);

    // The in-house agent changed this agent or its settings: reload what every
    // per-agent page reads (they all sit inside this menu).
    const agentRefresh = inject(AgentRefreshService);
    agentRefresh.on('bot_settings').pipe(takeUntilDestroyed()).subscribe(() => {
      tutorService.getTutorSettings(this.tutorId!).subscribe(() => {});
    });
    agentRefresh.on('bots').pipe(takeUntilDestroyed()).subscribe(() => {
      tutorService.getTutor(this.tutorId!).subscribe(() => {});
    });

    this.tutorsSub = this.tutorService.tutors$.subscribe(() => this.syncTutor());

    // Build sub-menu when settings arrive. Each sub-entry has its own URL
    // (`/ai-settings/<section>`) so Nebular's nb-menu can highlight the
    // active one via its built-in path matching — no manual `selected`
    // bookkeeping needed.
    this.items = this.buildItems([]);
    this.settingsSub = combineLatest([
      this.tutorService.tutorSettings$,
      this.tutorService.tutorTemplates$,
      this.tutorService.tutors$,
    ]).subscribe(([settings]) => this.syncItems(settings));

    // Re-sync `selected` flags on every navigation. We deliberately
    // mutate items in place (no array replacement) so nb-menu's own
    // re-render logic doesn't tear down the menu — we just toggle
    // which entry is highlighted. Without this, navigating between
    // sub-pages leaves the highlight on whatever was active when the
    // items were last rebuilt (memoization above keeps the array
    // stable across most settings emissions).
    this.routerSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        this.refreshSelected();
      });

    // A link from one agent's page to another agent keeps the shell (same
    // route config, new param): load the other agent in place.
    this.route.paramMap.pipe(takeUntilDestroyed()).subscribe((params) => {
      const id = params.get('tutorId');
      if (!id || id === this.tutorId) return;
      this.tutorId = id;
      this.loadAgent(id);
      this.syncTutor();
      this.syncItems(this.tutorService.tutorSettings$.value, true);
    });
  }

  /** Fetch the agent and its settings. Settings of ANOTHER agent are dropped
   *  first: tutorSettings$ is a global subject, and rendering stale items
   *  during the fetch window let auto-saves PATCH another bot's setting_ids
   *  against this bot's endpoint (500 "Invalid setting ID" burst when
   *  switching between bots). The same agent's settings stay — shown at once,
   *  refreshed in the background, so coming back to an agent does not blink. */
  private loadAgent(id: string): void {
    if (this.tutorService.tutorSettingsOwner !== id) {
      this.tutorService.clearTutorSettings();
    }
    // Cancel the previous agent's requests, or a late answer would refill
    // tutorSettings$ with its settings after the switch.
    this.settingsLoad?.unsubscribe();
    this.settingsLoad = this.tutorService.getTutorSettings(id).subscribe({ error: () => {} });
    this.tutorLoad?.unsubscribe();
    this.tutorLoad = this.tutorService.getTutor(id).subscribe({
      error: () => (this.loading = false),
    });
  }

  /** Page loader only while the agent itself is unknown (first visit). */
  private syncTutor(): void {
    this.tutor = this.tutorService.findTutor(this.tutorId!);
    this.loading = !this.tutor;
  }

  private syncItems(settings: TutorSettingItem[], force = false): void {
    const sectionKeys = filterSectionsByAvailableSettings(
      settings || [],
      { isLinkableTutor: this.isLinkableTutor() }
    )
      .map((s) => s.key)
      .join(',');
    // Skip the rebuild if the visible section list hasn't changed.
    // nb-menu re-creates its items when the input reference changes
    // and resets the active highlight (its auto-selection only
    // fires on RouterEvents, not on items reassignment) — so we
    // only swap when the structure genuinely needs updating (or the
    // agent changed: every link carries its id).
    if (!force && sectionKeys === this.lastSectionKeys) {
      return;
    }
    this.lastSectionKeys = sectionKeys;
    this.items = this.buildItems(settings);
    // After a rebuild the selected flags reflect the URL at the
    // moment of buildItems(); resync immediately so the highlight
    // matches the *current* URL even if the user navigated since.
    this.refreshSelected();
    // No detectChanges() here. We're inside a zone-tracked
    // subscribe callback so Angular runs CD anyway. Calling it
    // explicitly during Nebular's nb-menu update cycle triggers
    // its internal `Should be run in update mode` assertion and
    // spams the console on every settings emission.
    this.cdr.markForCheck();
  }

  /** Update each item's `selected` flag against the current URL. */
  private refreshSelected(): void {
    const path = window.location.pathname;
    const isActive = (link?: string) =>
      !!link && (path === link || path.startsWith(link + '/'));
    for (const item of this.items) {
      // Ein Elternpunkt mit gewähltem Unterpunkt ist ebenfalls „selected".
      // Die Optik entscheidet styles.scss („Seitenmenüs im Inhaltsbereich"):
      // aufgeklappt (`aria-expanded="true"`) nur dunkle Schrift, damit genau
      // der Unterpunkt eingedrückt liegt; zugeklappt ist der Unterpunkt
      // unsichtbar, und der Elternpunkt vertritt ihn eingedrückt. Vorher
      // stand hier `false` — zugeklappt war dann gar nichts markiert.
      const childActive = (item.children || []).some((c) => isActive(c.link));
      item.selected = childActive || isActive(item.link);
      for (const child of item.children || []) {
        child.selected = isActive(child.link);
      }
    }
    this.cdr.markForCheck();
  }

  /** True when the active bot's template can attach a dozent
   *  (alpha_tutor / alpha_flexible). Drives whether the dozent menu
   *  section appears as a sub-entry. */
  private isLinkableTutor(): boolean {
    if (!this.tutor?.template_id) return false;
    const template = this.tutorService.tutorTemplates$.value.find(
      (t) => t.id === this.tutor!.template_id
    );
    return !!template && (template.name === 'alpha_tutor' || template.name === 'alpha_flexible');
  }

  private buildItems(settings: TutorSettingItem[]): NbMenuItem[] {
    const available: SettingsSection[] = filterSectionsByAvailableSettings(
      settings || [],
      { isLinkableTutor: this.isLinkableTutor() }
    );

    // Read the current URL once so we can explicitly mark the matching
    // entry as `selected`. nb-menu auto-selects on router events, but
    // when the items array is replaced *between* navigations the
    // highlight is dropped — explicit `selected` keeps it consistent.
    const currentPath = window.location.pathname;
    const isActive = (link: string) =>
      currentPath === link || currentPath.startsWith(link + '/');

    // Abschnitte, die Reiter anderer Seiten sind (SECTIONS_ELSEWHERE: Wissen,
    // Lernprozess, Verhalten & Fähigkeiten), sind keine Unterpunkte der
    // KI-Konfiguration mehr.
    const aiSections = available.filter((sec) => !(sec.key in SECTIONS_ELSEWHERE));
    const aiChildren: NbMenuItem[] =
      aiSections.length > 1
        ? aiSections.map((sec) => {
            const link = `/tutors/${this.tutorId}/ai-settings/${sec.key}`;
            return {
              title: this.t.instant(sec.labelKey),
              link,
              selected: isActive(link),
            };
          })
        : [];

    const generalLink = `/tutors/${this.tutorId}/general`;
    const aiLink = `/tutors/${this.tutorId}/ai-settings`;
    const kbLink = `/tutors/${this.tutorId}/knowledge`;
    const learningLink = `/tutors/${this.tutorId}/learning`;
    const setupLink = `/tutors/${this.tutorId}/setup`;
    const linkedTutorsLink = `/tutors/${this.tutorId}/linked-tutors`;

    const items: NbMenuItem[] = [
      {
        title: this.t.instant('pages.tutors.tabGeneral'),
        icon: { icon: 'orbi', pack: 'al' },
        link: generalLink,
        selected: isActive(generalLink),
      },
      {
        title: this.t.instant('pages.tutors.tabAiConfiguration'),
        icon: 'settings-2-outline',
        link: aiLink,
        children: aiChildren.length ? aiChildren : undefined,
        expanded: aiChildren.length > 0,
        selected: aiChildren.length === 0 && isActive(aiLink),
      },
      {
        // „Wissen": Wissensbasis und „Quellen & Suche" als Reiter einer
        // Seite (/knowledge/<tab>).
        title: this.t.instant('pages.tutors.tabKnowledge'),
        icon: { icon: 'knowledge', pack: 'al' },
        link: kbLink,
        selected: isActive(kbLink),
      },
      {
        // „Lernprozess": Didaktik, Dozent, Lern-Interface, Lernerprofil und
        // Curriculum als Reiter einer Seite (/learning/<tab>); die Seite zeigt
        // nur die Reiter, die die Vorlage des Agenten hat.
        title: this.t.instant('pages.tutors.tabLearning'),
        icon: 'trending-up-outline',
        link: learningLink,
        selected: isActive(learningLink),
      },
      {
        // "Verhalten & Fähigkeiten": Learnings, Skills, MCP connectors and
        // Regeln & Grenzen as tabs of one page (/setup/<tab>); the page shows
        // only the tabs this agent's template supports.
        title: this.t.instant('pages.agentSetup.menu'),
        icon: { icon: 'agentSetup', pack: 'al' },
        link: setupLink,
        selected: isActive(setupLink),
      },
    ];

    // Access management is an admin/manager surface — the grant endpoints
    // reject everyone else, so hiding the entry keeps the menu honest rather
    // than offering a link that would only error.
    const role = this.auth.accessTokenPayload$.value?.role;
    if (
      role === UserRoles.Admin ||
      role === UserRoles.Manager ||
      role === UserRoles.SuperAdmin
    ) {
      const accessLink = `/tutors/${this.tutorId}/access`;
      items.push({
        title: this.t.instant('pages.tutors.tabAccess'),
        icon: 'shield-outline',
        link: accessLink,
        selected: isActive(accessLink),
      });
    }

    // alpha_dozent bots get an extra "Verlinkte Tutoren" entry showing
    // which tutors currently use this dozent and letting the admin add
    // more. Other templates don't get the entry — they don't have the
    // inverse relationship (tutors are the side that holds the link).
    if (this.isDozentBot()) {
      items.push({
        title: this.t.instant('pages.tutors.tabLinkedTutors'),
        icon: 'people-outline',
        link: linkedTutorsLink,
        selected: isActive(linkedTutorsLink),
      });
    }

    return items;
  }

  /** True when the active bot uses the alpha_dozent template. */
  private isDozentBot(): boolean {
    if (!this.tutor?.template_id) return false;
    const template = this.tutorService.tutorTemplates$.value.find(
      (t) => t.id === this.tutor!.template_id
    );
    return !!template && template.name === 'alpha_dozent';
  }

  // ── Orbi in der Kopfzeile: startet/beendet den Vorschau-Chat ──────
  // Ersetzt die Identitäts-Kachel und den runden Play-Button (Marcel,
  // 28.09.2026). Die Hover-/Klick-Mechanik kommt aus dem Orbi-Lab
  // (`play-button`), hier hängt nur der Chat daran.

  /** "Try me" alle ~10s, dazwischen normal schauen. */
  readonly headerOrbiScript = HEADER_ORBI_SCRIPT;

  /** Orbi-Typ zum Template des Agenten, wie auf der Agent-Kachel. Solange
   *  die Templates laden, `tutor` — `app-orbi-lab` zieht die Farben nach. */
  get orbType(): AgentOrbType {
    if (!this.tutor?.template_id) return 'tutor';
    const template = this.tutorService.tutorTemplates$.value.find(
      (t) => t.id === this.tutor!.template_id
    );
    return ORB_BY_TEMPLATE[template?.name ?? ''] || 'tutor';
  }

  /** Preview-chat state. `isChatActive$` is global (one preview at a
   *  time across the whole portal), matching how the agent cards read
   *  it — a chat started on another agent shows as running here too,
   *  and stopping it from here is the right action either way. */
  get isChatActive(): boolean {
    return this.chatPreviewService.isChatActive$.value;
  }

  /** Beide Handler sind idempotent: Orbi meldet auch dann einen Request,
   *  wenn er einem von außen gestarteten/beendeten Chat nur nachzieht. */
  startChat(): void {
    if (!this.isChatActive && this.tutorId) {
      this.chatPreviewService.connect(undefined, this.tutorId);
    }
  }

  stopChat(): void {
    if (this.isChatActive) this.chatPreviewService.closeChat();
  }

  ngAfterViewInit() {
    if (this.inline) return;
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    this.tutorsSub?.unsubscribe();
    this.settingsSub?.unsubscribe();
    this.routerSub?.unsubscribe();
    this.settingsLoad?.unsubscribe();
    this.tutorLoad?.unsubscribe();
  }
}
